import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import type { User, Course } from "../lib/types";
import { COURSES } from "../data/courses";
import { LECTURES, type Lecture } from "../data/lectures";
import { Sidebar } from "../components/Sidebar";
import { TopBar } from "../components/TopBar";
import { CourseArt } from "../components/CourseArt";
import { I } from "../components/Icons";
import { useProgress } from "../lib/progress";
import {
  courseProgress,
  completedCourseCount,
  inProgressCourseCount,
  isCourseComplete,
  isCourseUnlocked,
  previousCourse,
  resumeTarget,
  type ResumeTarget,
  TOTAL_COURSE_COUNT,
} from "../lib/course-progress";
import { usePresence, useOtherPeers, type Peer } from "../lib/presence";
import { PresenceAvatar } from "../components/PresenceAvatars";

interface Props {
  user: User;
  onSignOut: () => void;
}

export function Dashboard({ user, onSignOut }: Props) {
  const [, setLocation] = useLocation();
  const progress = useProgress();
  const completed = completedCourseCount(progress);
  const inProgress = inProgressCourseCount(progress);
  const resume = resumeTarget(progress);

  const presence = usePresence();
  useEffect(() => {
    presence.setLocation({ screen: "dashboard" });
  }, [presence]);

  // Which courses an admin has locked. Locked courses are grayed out and can't
  // be opened. Defaults to none until the fetch resolves.
  const [lockedIds, setLockedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    let alive = true;
    fetch("/api/courses/locks", { credentials: "include", cache: "no-store" })
      .then((r) => (r.ok ? r.json() : { locked: [] }))
      .then((data: { locked?: string[] }) => {
        if (alive) setLockedIds(new Set(data.locked ?? []));
      })
      .catch(() => {
        // On failure, leave everything unlocked — fail open for the learner.
      });
    return () => {
      alive = false;
    };
  }, []);

  // A course is closed to the learner if an admin locked it, OR if it's later in
  // the sequence and the previous course hasn't been marked complete yet.
  function isCourseLocked(id: string): boolean {
    return lockedIds.has(id) || !isCourseUnlocked(progress, id);
  }

  // The message explaining why a card is locked — instructor lock takes
  // precedence over the sequential "finish the previous course first" gate.
  function lockReason(id: string): string {
    if (lockedIds.has(id)) {
      return "This course is locked by your instructor.";
    }
    const prev = previousCourse(id);
    return prev
      ? `Mark Course ${prev.num} complete to unlock this one.`
      : "Locked.";
  }

  function openCourse(id: string) {
    if (isCourseLocked(id)) return;
    setLocation(`/lesson/${id}`);
  }

  // Which lectures an admin has locked. Locked lectures are grayed out and
  // can't be opened, exactly like locked courses. Defaults to none until the
  // fetch resolves.
  const [lockedLectureIds, setLockedLectureIds] = useState<Set<string>>(
    new Set(),
  );
  useEffect(() => {
    let alive = true;
    fetch("/api/lectures/locks", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { locked: [] }))
      .then((data: { locked?: string[] }) => {
        if (alive) setLockedLectureIds(new Set(data.locked ?? []));
      })
      .catch(() => {
        // On failure, leave everything unlocked — fail open for the learner.
      });
    return () => {
      alive = false;
    };
  }, []);

  function openLecture(id: string) {
    if (lockedLectureIds.has(id)) return;
    setLocation(`/lecture/${id}`);
  }

  const otherPeers = useOtherPeers();
  const peersByCourse = new Map<string, Peer[]>();
  for (const p of otherPeers) {
    if (p.location.screen === "lesson" && p.location.lessonId) {
      const arr = peersByCourse.get(p.location.lessonId) ?? [];
      arr.push(p);
      peersByCourse.set(p.location.lessonId, arr);
    }
  }

  const liveCourses: Course[] = COURSES.map((c) => {
    const pct = courseProgress(progress, c.id);
    const done = isCourseComplete(progress, c.id);
    const live: Course = {
      ...c,
      progress: pct,
      status: done ? "done" : pct > 0 ? "in-progress" : c.status,
    };
    // A lock (admin or sequential) overrides any progress-derived status, but a
    // completed course stays "done" — its lock is moot once finished.
    return !done && isCourseLocked(c.id) ? { ...live, status: "locked" } : live;
  });

  return (
    <div className="gf-shell">
      <Sidebar active="courses" user={user} onSignOut={onSignOut} />
      <div className="gf-main">
        <TopBar
          crumbs={[{ t: "Git Fit" }, { t: "My courses", strong: true }]}
        />
        <div className="gf-dash">
          <DashHero
            user={user}
            completed={completed}
            inProgress={inProgress}
            resume={resume}
            onResume={() => openCourse(resume.courseId)}
          />

          <div className="gf-dash__section-head">
            <div>
              <h2>The PM Git Track</h2>
              <p>Six short courses, in order. Each builds on the last.</p>
            </div>
          </div>

          {(() => {
            // Courses an admin lecture explicitly introduces are rendered in
            // that lecture's group, in lecture order. Anything no lecture
            // claims falls through to a final "More courses" group so nothing
            // silently disappears if the lectures and course list drift.
            const claimed = new Set(LECTURES.flatMap((l) => l.courseIds));
            const renderGroup = (ids: string[]) =>
              ids
                .map((id) => liveCourses.find((c) => c.id === id))
                .filter((c): c is Course => Boolean(c))
                .map((c, i) => (
                  <CourseCard
                    key={c.id}
                    course={c}
                    idx={i + 1}
                    peers={peersByCourse.get(c.id) ?? []}
                    lockReason={lockReason(c.id)}
                    onOpen={() => openCourse(c.id)}
                  />
                ));
            const leftover = liveCourses.filter((c) => !claimed.has(c.id));

            return (
              <>
                {LECTURES.map((lec) => (
                  <div className="gf-course-group" key={lec.id}>
                    <LectureBanner
                      lecture={lec}
                      locked={lockedLectureIds.has(lec.id)}
                      onOpen={() => openLecture(lec.id)}
                    />
                    <div className="gf-courses">{renderGroup(lec.courseIds)}</div>
                  </div>
                ))}
                {leftover.length > 0 && (
                  <div className="gf-course-group">
                    <div className="gf-courses">
                      {leftover.map((c, i) => (
                        <CourseCard
                          key={c.id}
                          course={c}
                          idx={i + 1}
                          peers={peersByCourse.get(c.id) ?? []}
                          lockReason={lockReason(c.id)}
                          onOpen={() => openCourse(c.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function DashHero({
  user,
  completed,
  inProgress,
  resume,
  onResume,
}: {
  user: User;
  completed: number;
  inProgress: number;
  resume: ResumeTarget;
  onResume: () => void;
}) {
  const firstName = user.name?.trim().split(/\s+/)[0] || "there";
  return (
    <div className="gf-dash__hero">
      <div className="gf-dash__hero-eyebrow">
        Welcome back, {firstName} · {completed} of {TOTAL_COURSE_COUNT} courses
        complete
      </div>
      <h1>You're three lessons away from confidently reviewing a PR.</h1>
      <p>
        Pick up where you left off, or jump into any course below. Your
        instructor will walk you through the rest live — Atlas keeps your
        progress in sync.
      </p>

      <div className="gf-dash__hero-stats">
        <div>
          <div className="gf-dash__hero-stat-val">{completed}</div>
          <div className="gf-dash__hero-stat-lbl">Completed</div>
        </div>
        <div>
          <div className="gf-dash__hero-stat-val">{inProgress}</div>
          <div className="gf-dash__hero-stat-lbl">In progress</div>
        </div>
        <div>
          <div className="gf-dash__hero-stat-val">38m</div>
          <div className="gf-dash__hero-stat-lbl">Time invested</div>
        </div>
        <div>
          <div className="gf-dash__hero-stat-val">12</div>
          <div className="gf-dash__hero-stat-lbl">Commits made</div>
        </div>
      </div>

      <div
        className="gf-dash__resume"
        onClick={onResume}
        style={{ cursor: "pointer" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gf-dash__resume-label">
            {resume.started ? "Resume" : "Start here"}
          </div>
          <div className="gf-dash__resume-title">
            {resume.courseTitle}
            {resume.stepTitle ? ` → ${resume.stepTitle}` : ""}
          </div>
        </div>
        <button className="gf-btn gf-btn--gradient">
          {resume.started ? "Continue" : "Start"}
          <I.ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

function LectureBanner({
  lecture,
  locked,
  onOpen,
}: {
  lecture: Lecture;
  locked: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      className={`gf-lecture-banner${locked ? " gf-lecture-banner--locked" : ""}`}
      onClick={onOpen}
      disabled={locked}
      aria-disabled={locked}
      title={locked ? "This lecture is locked by your instructor." : undefined}
    >
      <div className="gf-lecture-banner__icon">
        {locked ? <I.Lock size={18} /> : <I.Play size={18} />}
      </div>
      <div className="gf-lecture-banner__body">
        <div className="gf-lecture-banner__eyebrow">{lecture.eyebrow}</div>
        <div className="gf-lecture-banner__title">{lecture.title}</div>
        <div className="gf-lecture-banner__sub">{lecture.subtitle}</div>
      </div>
      <div className="gf-lecture-banner__cta">
        {locked ? (
          <>
            <I.Lock size={14} /> Locked
          </>
        ) : (
          <>
            Open lecture
            <I.ArrowRight size={14} />
          </>
        )}
      </div>
    </button>
  );
}

interface CardProps {
  course: Course;
  idx: number;
  peers: Peer[];
  lockReason?: string;
  onOpen: () => void;
}

function CourseCard({ course, peers, lockReason, onOpen }: CardProps) {
  const pct = Math.round(course.progress * 100);
  const locked = course.status === "locked";
  return (
    <button
      className={`gf-course gf-course--${course.num}${locked ? " gf-course--locked" : ""}`}
      onClick={onOpen}
      disabled={locked}
      aria-disabled={locked}
      title={locked ? lockReason : undefined}
    >
      {locked && (
        <div className="gf-course__badge gf-course__badge--locked">
          <I.Lock size={10} style={{ marginRight: 4, verticalAlign: "-2px" }} />
          Locked
        </div>
      )}
      {!locked && course.badge && (
        <div className="gf-course__badge gf-course__badge--new">
          {course.badge}
        </div>
      )}
      {!locked && course.status === "done" && (
        <div className="gf-course__badge gf-course__badge--done">
          <I.Check size={10} style={{ marginRight: 4, verticalAlign: "-2px" }} />
          Done
        </div>
      )}
      {peers.length > 0 && (
        <div className="gf-course__peers" title={`${peers.length} learner${peers.length === 1 ? "" : "s"} here now`}>
          {peers.slice(0, 3).map((p) => (
            <PresenceAvatar key={p.sessionId} peer={p} size={24} />
          ))}
          {peers.length > 3 && (
            <div className="gf-presence__avatar gf-presence__avatar--more">
              +{peers.length - 3}
            </div>
          )}
        </div>
      )}
      <div className="gf-course__art">
        <CourseArt kind={course.art} />
      </div>
      <div className="gf-course__body">
        <div className="gf-course__meta">
          <span className="gf-course__num">Course {course.num}</span>
          <span>
            {course.duration} · {course.lessons} lessons
          </span>
        </div>
        <div className="gf-course__title">{course.title}</div>
        <p className="gf-course__desc">{course.desc}</p>
        <div className="gf-course__progress">
          <div className="gf-course__progress-row">
            <span className="gf-course__progress-lbl">
              {course.status === "done"
                ? "Completed"
                : course.status === "locked"
                  ? "Locked"
                  : "Progress"}
            </span>
            <span className="gf-course__progress-val">{pct}%</span>
          </div>
          <div className="gf-course__progress-bar">
            <div
              className="gf-course__progress-bar-fill"
              style={{
                width: `${pct}%`,
                background:
                  course.status === "done" ? "var(--success)" : undefined,
              }}
            ></div>
          </div>
        </div>
      </div>
    </button>
  );
}
